import { Economy, LevelCatalog } from "@highmarch/app";
import type { HubApi, HubWidgetHandle } from "../../features/registry";
import { bump, motionOk } from "../kit";
import { toast } from "../toast";
import { flyReward } from "./ordersSheet";
import { fmtBadgeEta } from "./badges";
import { econCue } from "./purchaseFx";
import { coinsSvg } from "./econArt";
import { receiptLine } from "./shopActions";
import { appHeight } from "../appSize";

/**
 * Fortress Tribute on the hub scene (FINISH_PLAN WP5, M16; meta.json `tribute`): a chip pinned beside the fortress
 * (HubApi.anchor, the scene's anchorOnScreen) with a fill ring; FULL glows and bobs. A tap claims what it holds — the
 * coins burst from the chip into the Gold pill, the chip pops, a toast says what came in. A tap on an empty chip
 * says when it fills. Never sold, never boosted; it shows once the unlock level is cleared.
 */

/** Gap between the builder chip and this one, which sits to its right on the same line (CSS px). */
const GAP = 6;
/** Kept this far from the screen's right edge; with no room there, it drops under the builder chip. */
const EDGE = 8;
/** The chips keep this far below the top bar (its second row holds the gems and Marks pills). */
const CLEAR = 6;
/** Fallback when the scene cannot say where the fortress is: the builder chip's own fallback point. */
const FALLBACK = { x: 0.5, y: 0.62 } as const;
const PERCENT = 100;

export interface TributeView { readonly unlocked: boolean; readonly fraction: number; readonly full: boolean; readonly fullIn: number; readonly total: number }

/** What the chip shows, as data (tributeChip is tested through it). */
export function tributeView(catalog: LevelCatalog, now: number): TributeView {
  const eco = new Economy(catalog), t = eco.tribute(now), tr = catalog.economy.tribute, cap = tr.capSeconds;
  const filled = tr.filledSeconds(now);
  return { unlocked: t.unlocked, fraction: cap > 0 ? filled / cap : 0, full: t.full, fullIn: Math.max(0, cap - filled), total: t.pay };
}

export function mountTributeChip(hub: HubApi, layer: HTMLElement, catalog: LevelCatalog, now: () => number): HubWidgetHandle {
  const chip = document.createElement("button");
  chip.className = "tchip hidden";
  chip.setAttribute("aria-label", "Fortress Tribute");
  layer.append(chip);
  let shown = "";

  const update = (): void => {
    const v = tributeView(catalog, now());
    chip.classList.toggle("hidden", !v.unlocked);
    if (!v.unlocked) return;
    const pctFill = Math.round(Math.min(1, v.fraction) * PERCENT);
    const html = `<span class="ring" style="--p:${pctFill}%">${coinsSvg(22)}</span><span class="t">${v.full ? "Full!" : `${pctFill}%`}</span>`;
    if (html !== shown) { chip.innerHTML = html; shown = html; }
    chip.classList.toggle("full", v.full);
  };

  /**
   * One layout for the hub's chips, top to bottom: the top bar, then the event chip (features/events.ts pins it under
   * the bar on the left, independent of the others), then the builder chip on the fortress with this one beside it on
   * its right; under it when the screen has no room; on the fortress itself when the builder chip is hidden. The
   * builder/Tribute pair is pushed below whatever sits above it on the same columns, so no chip lands on another.
   * Re-run every tick: the builder chip's width changes with its text ("Idle", "2h 13m"), the event chip comes and goes.
   */
  /** The lowest a chip may reach: just above the battle card (the dock). */
  const floor = (): number => (layer.closest(".hub")?.querySelector(".dock")?.getBoundingClientRect().top ?? appHeight()) - CLEAR;
  const layout = (): void => {
    const barBottom = (layer.closest(".hub")?.querySelector(".topbar")?.getBoundingClientRect().bottom ?? 0) + CLEAR;
    const w = chip.getBoundingClientRect().width;
    const ev = layer.querySelector<HTMLElement>(".evhubchip:not([hidden])")?.getBoundingClientRect();
    /** The lowest edge above a chip spanning [left, right]: the top bar, or the event chip when it shares those columns. */
    const ceiling = (left: number, right: number): number => (ev && ev.height > 0 && ev.left < right && ev.right > left ? Math.max(barBottom, ev.bottom + CLEAR) : barBottom);
    const bEl = layer.querySelector<HTMLElement>(".bchip:not(.hidden)");
    if (bEl) {
      // On a short screen the fortress stands high enough that the builder chip would sit on the top bar's second pill
      // row or the event chip: nudge it down (the `translate` property adds to the hub's own `transform`, set every tick).
      bEl.style.translate = "";
      const r = bEl.getBoundingClientRect();
      // ...but never onto the battle card: the push stops at the card's rim
      const push = Math.min(ceiling(r.left, r.right + GAP + w) - r.top, floor() - r.bottom);
      if (push > 0) bEl.style.translate = `0 ${Math.ceil(push)}px`;
    }
    const b = bEl?.getBoundingClientRect();
    if (b && b.width > 0) {
      // beside the builder chip on its right; with no room there, on its left; under it only when neither fits
      const right = b.right + GAP + w <= innerWidth - EDGE, left = !right && b.left - GAP - w >= EDGE;
      const line = b.top + (b.height - chip.offsetHeight) / 2;
      const x = right ? b.right + GAP : left ? b.left - GAP - w : b.left + b.width / 2 - w / 2;
      const y = right || left ? line : Math.min(b.bottom + GAP, floor() - chip.offsetHeight);
      chip.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
      return;
    }
    const at = hub.anchor("fortress") ?? { x: innerWidth * FALLBACK.x, y: appHeight() * FALLBACK.y };
    const y = Math.min(Math.max(at.y, ceiling(at.x - w / 2, at.x + w / 2) + chip.offsetHeight), floor());
    chip.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(y)}px) translate(-50%, -100%)`;
  };

  chip.onclick = () => {
    const v = tributeView(catalog, now());
    const pay = new Economy(catalog).claimTribute(now());
    if (!pay) { toast(hub.parent, `Fortress Tribute: gold piles up here while you're away · full in ${fmtBadgeEta(v.fullIn)}`); return; }
    econCue("tribute");
    hub.changed();                                     // saves; the pills now show the new totals, the flight ticks them
    flyReward(chip, pay);
    burst(chip);
    toast(hub.parent, `Tribute collected: ${receiptLine(pay, 0)}`);
  };

  update();
  layout();
  return { update: () => { update(); layout(); }, layout, dispose: () => { chip.remove(); layer.querySelector<HTMLElement>(".bchip")?.style.removeProperty("translate"); } };
}

/** The claim's pop: the chip squashes and a ring of light spreads from it (transform/opacity only). */
function burst(chip: HTMLElement): void {
  bump(chip, 1.25, 260);
  if (!motionOk()) return;
  const ring = document.createElement("span");
  ring.className = "tburst";
  chip.append(ring);
  const LIFE_MS = 600;
  window.setTimeout(() => ring.remove(), LIFE_MS);
}

/** The Tribute's reminder copy (features/economy.ts schedules it for the moment it fills). */
export const TRIBUTE_REMINDER = { title: "Tribute is full", body: "The coffers are brimming, Commander. Come and collect." } as const;
